export const practiceProfileMarginsNoNav = "mx-23 mt-[59px] mb-17 box-border"
export const practiceProfileMarginWithNav = "mx-20 mt-[2px] mb-17 box-border"
export const titleInputSpacer = "flex flex-col w-full md:w-1/2 space-y-2"
export const formSpacing = "flex flex-col space-y-6"
export const practiceSaveButtonTopPadding = "pt-[31px]"

/**
 * Individual component and layout padding measurements used to compute page-level minimum widths.
 *
 * These values exist because padding and sizing are not applied uniformly across the app —
 * profile cards, collapsible cards, and breadcrumb layouts each carry slightly different
 * horizontal padding. Rather than hardcoding magic numbers at each usage site, the constants
 * below capture the measured values so that the composed minimum widths in
 * `collapsibleCardMinWidths` and `navChildMinWidths` remain traceable and easy to update.
 *
 * The goal is to make dropdowns, accordions, file uploads, and tables behave consistently
 * (triggering scroll or wrapping at the right breakpoint) while still playing nicely with
 * the existing form components that surround them.
 */
export const dropzoneMinWidth = 360
export const inputMinWidth = 350
export const caseViewControlWidth = 474
const fileUploadCardXPadding = 2 * 33
const collapsibleCardPadding = 2 * 17
const collapsibleCardAdditionalPadding = 2 * 20
const profileWideXPadding = 2 * 108
const profileWithBreadCrumbsWideXPadding = 2 * 97
const profileNarrowXPadding = 2 * 26
const providerProfileAdditionalXPadding = 2 * 26
export const dropzoneMinWidths = {
  standard: dropzoneMinWidth,
  withBorder: dropzoneMinWidth + fileUploadCardXPadding,
}
export const collapsibleCardMinWidths = {
  standard: inputMinWidth + collapsibleCardPadding,
  withDropzone: dropzoneMinWidths.withBorder + collapsibleCardPadding,
  withAdditionalPaddingAndDropzone:
    dropzoneMinWidths.withBorder +
    collapsibleCardPadding +
    collapsibleCardAdditionalPadding,
}

/**
 * Per-page minimum widths for the direct children of navigation wrapper components
 * (e.g. `MainNavigation`, `WizardNavigation`).
 *
 * ## Why this exists
 *
 * Tailwind's JIT compiler performs a static scan of source files to generate CSS. Class names
 * constructed at runtime — e.g. `` `min-w-[${value}px]` `` — are invisible to the scanner and
 * never emit a CSS rule, even though the class string appears in the DOM. To work around this,
 * minimum widths are stored here as plain numbers and applied via React's `style` prop
 * (e.g. `style={{ minWidth: navChildMinWidths[key] + "px" }}`), which bypasses the JIT scanner
 * entirely.
 *
 * ## Pattern
 *
 * Apply the appropriate `MIN_WIDTH_NAV_CHILDREN` key on the **direct child container** of
 * a navigation wrapper. The navigation wrapper itself handles layout; the child enforces the
 * minimum width so that inner components (tables, file uploads, accordions) scroll horizontally
 * rather than collapsing or reflowing below their usable minimum size.
 */
export enum MIN_WIDTH_NAV_CHILDREN {
  MANAGED_PRACTICES = "managedPractices",
  PRACTICE_LIABILITY_V2 = "practiceLiabilityV2",
  PRACTICE_INDIVIDUALS = "practiceIndividuals",
  PRACTICE_INDIVIDUALS_EDIT = "practiceIndividualsEdit",
  PRACTICE_LOCATION_DETAILS = "practiceLocationDetails",
  PRACTICE_TAX_W9 = "practiceTaxW9",
  PROVIDER_DISCLOSURE = "providerDisclosure",
  PROVIDER_BIO_PHOTO = "providerBioPhoto",
  PROVIDER_CREDENTIALS = "providerCredentials",
  PROVIDER_LICENSES = "providerLicenses",
  PROVIDER_LIABILITY_INSURANCE = "providerLiabilityInsurance",
  INVITATIONS_ACCEPT = "invitationsAccept",
  REFERRALS = "referrals",
  CASE_VIEW_CONTROL = "caseViewControl",
}
export const navChildMinWidths: Record<MIN_WIDTH_NAV_CHILDREN, number> = {
  [MIN_WIDTH_NAV_CHILDREN.MANAGED_PRACTICES]:
    collapsibleCardMinWidths.standard + profileWideXPadding,
  [MIN_WIDTH_NAV_CHILDREN.PRACTICE_LIABILITY_V2]:
    collapsibleCardMinWidths.withDropzone + profileWideXPadding,
  [MIN_WIDTH_NAV_CHILDREN.PRACTICE_INDIVIDUALS]:
    collapsibleCardMinWidths.standard + profileNarrowXPadding,
  [MIN_WIDTH_NAV_CHILDREN.PRACTICE_INDIVIDUALS_EDIT]:
    collapsibleCardMinWidths.withAdditionalPaddingAndDropzone +
    profileNarrowXPadding,
  [MIN_WIDTH_NAV_CHILDREN.PRACTICE_LOCATION_DETAILS]:
    collapsibleCardMinWidths.withDropzone + profileWithBreadCrumbsWideXPadding,
  [MIN_WIDTH_NAV_CHILDREN.PRACTICE_TAX_W9]:
    collapsibleCardMinWidths.withDropzone + profileWithBreadCrumbsWideXPadding,
  [MIN_WIDTH_NAV_CHILDREN.PROVIDER_DISCLOSURE]:
    collapsibleCardMinWidths.withDropzone +
    profileWideXPadding +
    providerProfileAdditionalXPadding,
  [MIN_WIDTH_NAV_CHILDREN.PROVIDER_BIO_PHOTO]:
    collapsibleCardMinWidths.withDropzone + profileWideXPadding,
  [MIN_WIDTH_NAV_CHILDREN.PROVIDER_CREDENTIALS]:
    collapsibleCardMinWidths.withDropzone + profileWideXPadding,
  [MIN_WIDTH_NAV_CHILDREN.PROVIDER_LICENSES]:
    collapsibleCardMinWidths.withDropzone + profileWideXPadding,
  [MIN_WIDTH_NAV_CHILDREN.PROVIDER_LIABILITY_INSURANCE]:
    collapsibleCardMinWidths.withAdditionalPaddingAndDropzone +
    profileWideXPadding,
  [MIN_WIDTH_NAV_CHILDREN.INVITATIONS_ACCEPT]:
    collapsibleCardMinWidths.withAdditionalPaddingAndDropzone +
    profileNarrowXPadding,
  [MIN_WIDTH_NAV_CHILDREN.REFERRALS]:
    collapsibleCardMinWidths.standard + profileWithBreadCrumbsWideXPadding,
  [MIN_WIDTH_NAV_CHILDREN.CASE_VIEW_CONTROL]:
    caseViewControlWidth + profileWideXPadding,
}
